import FormattedMessage from 'I18n/components/FormattedMessage';
import getLang from 'I18n/utils/getLang';
import { Button } from 'foundations-components/transitional/components/Button';
import { FloatingAlertList } from 'foundations-components/transitional/components/FloatingAlertList';
import { useAppInstallability } from 'marketplace-ui-apps-core/installability/hooks/useAppInstallability';
import { getDataFetchingClientRequestState } from 'marketplace-ui-common/dataFetchingClient/utils/getDataFetchingClientRequestState';
import LoadingWrapper from 'marketplace-ui-common/layout/components/LoadingWrapper';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FeatureCompatibilityModal } from './compatibility/FeatureCompatibilityModal';
import { DetailsPageContext } from './context/DetailsPageContext';
import { DetailsError } from './error/DetailsError';
import { useListingPreview } from './hooks/useListingPreview';
import { useTracking } from './hooks/useTracking';
import { useUnifiedDetails } from './hooks/useUnifiedDetails';
import { AgentCTAButton } from './install/AgentCTAButton';
import { AppCTAButton } from './install/AppCTAButton';
import { DetailsPage } from './layout/DetailsPage';
import { ProductBuyBox } from './layout/cards/ProductBuyBox';
import { ProductFeaturesCard } from './layout/cards/ProductFeaturesCard';
import { ProductFeedbackCard } from './layout/cards/ProductFeedbackCard';
import { ProductFunctionalityCard } from './layout/cards/ProductFunctionalityCard';
import { ProductInfoCard } from './layout/cards/ProductInfoCard';
import { ProductOverviewCard } from './layout/cards/ProductOverviewCard';
import { ProductPricingCard } from './layout/cards/ProductPricingCard';
import { ProductRequirementsCard } from './layout/cards/ProductRequirementsCard';
import { ProductReviewsCard } from './layout/cards/ProductReviewsCard';
import { generateLeadWithAction } from './leadGen/leadGenClient';
import { HSPageViewTracker } from './utils/HSPageViewTracker';

// @ts-expect-error ts-migrate(2307) FIXME: Cannot find module 'bender-url!ecosystem-marketpla... Remove this comment to see the full error message
import fallbackAgentOverviewImage from 'bender-url!marketplace-ui-details/img/agent-overview-fallback.png';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function Details({
  appContext,
  logInUrl,
  // To be used in the Public Marketplace when the CTA reads "Sign in"
  previewListingId,
  // Will be used to fetch listing preview data
  renderedErrorLink,
  slug,
  userInfo,
  isEmbedded = false,
  onInstallComplete,
  onInstallError
}) {
  var _userInfo$user;
  const published = !!slug && !previewListingId;
  const isPreview = !!previewListingId;
  const [compatibilityModalOpen, setCompatibilityModalOpen] = useState(false);
  const {
    pathname
  } = useLocation();

  // This will be the only call to Unified Details Service
  // Once everything is migrated to V3
  const {
    unifiedDetailsData: unifiedDetailsDataV3,
    unifiedDetailsLoading: unifiedDetailsLoadingV3,
    unifiedDetailsError: unifiedDetailsErrorV3
  } = useUnifiedDetails({
    appContext,
    slug: slug || '',
    language: getLang(),
    skip: !slug || isPreview,
    version: 3
  });

  // Additional call to Unified Details Service V2
  // To supplement with App fields that are not available in V3
  const {
    unifiedDetailsData: unifiedDetailsDataV2,
    unifiedDetailsLoading: unifiedDetailsLoadingV2,
    unifiedDetailsError: unifiedDetailsErrorV2
  } = useUnifiedDetails({
    appContext,
    slug: slug || '',
    language: getLang(),
    skip: !slug || isPreview,
    version: 2
  });
  const {
    previewData,
    previewLoading,
    previewError
  } = useListingPreview({
    appContext,
    listingId: previewListingId,
    skip: !!slug || !isPreview
  });
  const unifiedDetailsRequestStatusV3 = getDataFetchingClientRequestState(unifiedDetailsErrorV3, unifiedDetailsLoadingV3);
  const unifiedDetailsRequestStatusV2 = getDataFetchingClientRequestState(unifiedDetailsErrorV2, unifiedDetailsLoadingV2);
  const previewRequestStatus = getDataFetchingClientRequestState(previewError, previewLoading);

  // UDS V3 data from Framework
  const {
    agentType,
    agencyPricingUrl,
    betaDetails,
    builtByHubSpot,
    caseStudyUrl,
    category,
    certifiedAt,
    companyName,
    companyUrl,
    contactForm,
    currency,
    facebookUrl,
    features: frameworkFeaturesV3,
    installCount: frameworkInstallCount,
    // Not yet returning for apps
    installUrl,
    languages,
    listingIcon,
    listingId: frameworkListingId,
    liveChatUrl,
    meetingsUrl,
    name,
    overview,
    partnerPortalId,
    phone,
    pricingPlans,
    pricingPlansUrl,
    privacyUrl1,
    privacyUrl2,
    privacyUrl3,
    productFeatures: frameworkProductFeatures,
    productType,
    screenshots: frameworkScreenshots,
    setupDocsUrl,
    sharedDataInfo,
    sharedDataHow,
    sharedObjects,
    // New V3 sharedObjects data
    sourceId: frameworkSourceId,
    // Not yet returning for apps
    supportEmail,
    supportLanguages,
    supportUrl,
    tagline,
    termsUrl,
    videos: frameworkVideos
  } = previewData || unifiedDetailsDataV3;

  // Remaining App Details data from Unified Details Service V2
  const {
    dashworks,
    features: frameworkFeaturesV2,
    id: ecosystemListingId,
    integrationFeatures,
    neededScopeGroups,
    opsHub,
    offering,
    offeringId,
    reviewSummary,
    tiers,
    uie
  } = previewData || unifiedDetailsDataV2;
  const {
    connectionType,
    installsCount: ecosystemInstallCount,
    sourceId: ecosystemSourceId
  } = offering || {};
  const listingId = useMemo(() => {
    if (productType === 'AGENT') {
      return frameworkListingId;
    } else if (productType === 'APP') {
      return ecosystemListingId;
    }
  }, [productType, frameworkListingId, ecosystemListingId]);
  const sourceId = useMemo(() => {
    if (productType === 'AGENT') {
      return frameworkSourceId;
    } else if (productType === 'APP') {
      return ecosystemSourceId;
    }
  }, [productType, frameworkSourceId, ecosystemSourceId]);
  const screenshots = useMemo(() => {
    // Filter out screenshot objects with empty values
    const screenshotsWithValues = frameworkScreenshots === null || frameworkScreenshots === void 0 ? void 0 : frameworkScreenshots.filter(s => s.screenshot.value);
    return screenshotsWithValues !== null && screenshotsWithValues !== void 0 && screenshotsWithValues.length ? screenshotsWithValues :
    // Default to agent fallback image
    productType === 'AGENT' ? [{
      screenshot: {
        value: fallbackAgentOverviewImage,
        altText: ''
      }
    }] : [];
  }, [frameworkScreenshots, productType]);
  const videos = useMemo(() => {
    // Filter out video objects with empty values
    return frameworkVideos === null || frameworkVideos === void 0 ? void 0 : frameworkVideos.filter(s => s.video.value);
  }, [frameworkVideos]);
  const featuresV2 = useMemo(() => {
    // Filter out feature objects with empty values
    return frameworkFeaturesV2 === null || frameworkFeaturesV2 === void 0 ? void 0 : frameworkFeaturesV2.filter(feature => feature.title);
  }, [frameworkFeaturesV2]);
  const featuresV3 = useMemo(() => {
    // Filter out feature objects with empty values
    return frameworkFeaturesV3 === null || frameworkFeaturesV3 === void 0 ? void 0 : frameworkFeaturesV3.filter(feature => feature.featureName);
  }, [frameworkFeaturesV3]);
  const tools = useMemo(() => {
    return frameworkProductFeatures === null || frameworkProductFeatures === void 0 ? void 0 : frameworkProductFeatures.filter(feature => feature.data.isTool);
  }, [frameworkProductFeatures]);
  const appObjects = useMemo(() => {
    return frameworkProductFeatures === null || frameworkProductFeatures === void 0 ? void 0 : frameworkProductFeatures.filter(feature => feature.type === 'APP_OBJECT');
  }, [frameworkProductFeatures]);
  const appCards = useMemo(() => {
    return frameworkProductFeatures === null || frameworkProductFeatures === void 0 ? void 0 : frameworkProductFeatures.filter(feature => feature.type === 'UIE' &&
    // if any of these fields are missing, don't render the card
    feature.name && feature.data.description && feature.data.previewImage && feature.data.eligibleLocations);
  }, [frameworkProductFeatures]);

  /**
   * Memo below determines the installType for APP products
   * Unified Details Service V3 will directly return the installType for APP products
   * But until then, we're calculating it on the FE to have the future proof expected prop for the AppCTAButton
   *
   * installType replaces ConnectButton.tsx logic that considered:
   * - homemade (defined in Admin Tool)
   * - connectionType (HOMEMADE_IFRAME, HOMEMADE_MODAL, HOMEMADE_EXTERNAL, STANDARD)
   * - opsHub (Data Sync team API)
   * - isDashworks (Data Sync team API)
   * - appUrl (Listing Editor)
   *
   * New installType values:
   * - HOMEMADE_DATA_SYNC: For Data Sync apps (detected via opsHub field)
   * - HOMEMADE_DASHWORKS: For Dashworks apps (detected via dashworks field)
   * - STANDARD: OAuth redirect flow (connectionType=STANDARD without appUrl, we make an additional API call to get the OAuth URL)
   * - LEGACY: Legacy appUrl redirect (connectionType=STANDARD with appUrl)
   * - HOMEMADE_EXTERNAL: connectionType=HOMEMADE_EXTERNAL
   * - HOMEMADE_MODAL: connectionType=HOMEMADE_MODAL
   * - HOMEMADE_IFRAME: connectionType=HOMEMADE_IFRAME
   */
  const appInstallType = useMemo(() => {
    // Check for Data Sync apps (opsHub field from Data Sync team API)
    if (opsHub) {
      return 'HOMEMADE_DATA_SYNC';
    }

    // Check for Dashworks apps (dashworks field from Data Sync team API)
    if (dashworks) {
      return 'HOMEMADE_DASHWORKS';
    }

    // Standard connection type handling
    if (connectionType === 'STANDARD') {
      // Standard OAuth flow - no appInstallUrl means use OAuth redirect
      if (!installUrl) {
        return 'STANDARD';
      }
      // Legacy flow - has appInstallUrl, use direct redirect
      return 'LEGACY';
    }

    // Homemade connection types
    if (connectionType === 'HOMEMADE_EXTERNAL') {
      return 'HOMEMADE_EXTERNAL';
    }
    if (connectionType === 'HOMEMADE_MODAL') {
      return 'HOMEMADE_MODAL';
    }
    if (connectionType === 'HOMEMADE_IFRAME') {
      return 'HOMEMADE_IFRAME';
    }
    return null;
  }, [connectionType, opsHub, dashworks, installUrl]);
  const {
    trackInstall,
    trackInteraction,
    trackPageViewed
  } = useTracking({
    listingId,
    sourceId,
    slug: slug || '',
    type: productType,
    appContext,
    embed: isEmbedded,
    isPreview
  });
  useEffect(() => {
    if (isEmbedded) {
      //page view already tracked on storefront for non-embedded implementation
      trackPageViewed();
    }
  }, [isEmbedded, trackPageViewed]);
  const runLeadGen = useCallback(({
    action
  }) => {
    if (productType !== 'APP' || appContext !== 'app' || builtByHubSpot || !published) {
      return;
    }
    trackInteraction({
      action: 'generated lead',
      value: `${partnerPortalId}`
    });
    if (action && offeringId) {
      generateLeadWithAction({
        action,
        offeringId
      }).catch(error => {
        console.error('Error sending lead:', error);
      });
    }
  }, [appContext, builtByHubSpot, offeringId, partnerPortalId, productType, published, trackInteraction]);

  // This info will be returned from the Unified Installability Service
  // But for now, we're using the separate apps-specific endpoint/hook
  // To get real data for the page
  const {
    installability,
    missingCompatibilityRequirements
  } = useAppInstallability({
    offeringId,
    appContext,
    features: frameworkFeaturesV2
  });
  const renderedPrimaryCTA = useMemo(() => {
    // Basic disabled button for preview
    // Note: Apps with STANDARD installType are allowed to be previewed
    if (isPreview && (productType === 'AGENT' || productType === 'APP' && appInstallType !== 'STANDARD')) {
      return /*#__PURE__*/_jsx(Button, {
        use: "primary",
        disabled: true,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `detailsPage.productDetailsCard.cta.install.${productType}`
        })
      });
    }

    // Public "Sign in" buttons that redirect to portal picker
    if (appContext === 'public') {
      return /*#__PURE__*/_jsx(Button, {
        use: "primary",
        href: logInUrl,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `detailsPage.productDetailsCard.cta.signIn.${productType}`
        })
      });
    }
    if (productType === 'AGENT') {
      return /*#__PURE__*/_jsx(AgentCTAButton, {
        agentType: agentType,
        betaDetails: betaDetails,
        installUrl: installUrl,
        onInstallComplete: onInstallComplete,
        onInstallError: onInstallError,
        sourceId: sourceId
      });
    }
    if (productType === 'APP' && appInstallType) {
      return /*#__PURE__*/_jsx(AppCTAButton, {
        features: frameworkFeaturesV2,
        installType: appInstallType,
        installUrl: installUrl,
        isUIE: uie,
        offeringId: offeringId,
        sourceId: sourceId,
        onInstallComplete: onInstallComplete,
        onInstallError: onInstallError
      });
    }
    return null;
  }, [isPreview, productType, appInstallType, appContext, logInUrl, agentType, betaDetails, installUrl, onInstallComplete, onInstallError, sourceId, frameworkFeaturesV2, uie, offeringId]);
  const unifiedDetailsError = unifiedDetailsErrorV2 || unifiedDetailsErrorV3;
  if (unifiedDetailsError) {
    return /*#__PURE__*/_jsx(DetailsError, {
      renderedErrorLink: renderedErrorLink,
      unifiedDetailsError: unifiedDetailsError
    });
  }
  return /*#__PURE__*/_jsx(LoadingWrapper, {
    requestStatus: [unifiedDetailsRequestStatusV2, unifiedDetailsRequestStatusV3, previewRequestStatus],
    children: /*#__PURE__*/_jsxs(DetailsPageContext.Provider, {
      value: {
        appContext,
        iconUrl: listingIcon === null || listingIcon === void 0 ? void 0 : listingIcon.value,
        listingId,
        name,
        productType,
        published,
        runLeadGen,
        trackInteraction,
        trackInstall,
        userInfo,
        isEmbedded
      },
      children: [/*#__PURE__*/_jsx(FloatingAlertList, {}), /*#__PURE__*/_jsx(HSPageViewTracker, {
        pathname: pathname,
        overrideTrack: appContext === 'app',
        addLang: appContext === 'app'
      }), /*#__PURE__*/_jsx(DetailsPage, {
        leftColumn: /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(ProductBuyBox, {
            betaDetails: betaDetails,
            certifiedAt: certifiedAt,
            companyName: companyName || 'HubSpot',
            companyUrl: companyUrl,
            contactForm: contactForm
            // Framework data || Fallback App Details data
            ,
            installCount: frameworkInstallCount || ecosystemInstallCount,
            meetingsUrl: meetingsUrl,
            reviewSummary: reviewSummary,
            tagline: tagline,
            primaryCTA: renderedPrimaryCTA
          }), /*#__PURE__*/_jsx(ProductRequirementsCard, {
            connectionType: connectionType,
            features: featuresV2,
            builtByHubSpot: builtByHubSpot,
            installability: installability,
            missingCompatibilityRequirements: missingCompatibilityRequirements,
            neededScopeGroups: neededScopeGroups,
            onCompatibilityModalOpen: () => setCompatibilityModalOpen(true),
            pricingPlans: pricingPlans || [],
            tiers: tiers || []
          })]
        }),
        rightColumn: /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(ProductOverviewCard, {
            caseStudyUrl: caseStudyUrl,
            overview: overview,
            screenshots: screenshots,
            setupDocsUrl: setupDocsUrl,
            videos: videos
          }), /*#__PURE__*/_jsx(ProductFeaturesCard
          // Memoized, sanitized features
          , {
            features: featuresV3,
            integrationFeatures: integrationFeatures,
            missingCompatibilityRequirements: missingCompatibilityRequirements,
            onCompatibilityModalOpen: () => setCompatibilityModalOpen(true),
            showCompatibility: !!installability
          }), /*#__PURE__*/_jsx(ProductFunctionalityCard, {
            sharedDataHow: sharedDataHow,
            sharedDataInfo: sharedDataInfo,
            sharedObjects: sharedObjects,
            appObjects: appObjects,
            tools: tools,
            appCards: appCards
          }), productType !== 'AGENT' && /*#__PURE__*/_jsx(ProductReviewsCard, {
            isPreview: isPreview,
            partnerPortalId: partnerPortalId,
            productId: offeringId,
            supportUrl: supportUrl,
            userId: (userInfo === null || userInfo === void 0 || (_userInfo$user = userInfo.user) === null || _userInfo$user === void 0 ? void 0 : _userInfo$user.user_id) || 0
          }), /*#__PURE__*/_jsx(ProductPricingCard, {
            agencyPricingUrl: agencyPricingUrl,
            betaDetails: betaDetails,
            currency: currency,
            pricingPlansUrl: pricingPlansUrl,
            pricingPlans: pricingPlans || []
          }), /*#__PURE__*/_jsx(ProductInfoCard, {
            details: {
              companyName: companyName || 'HubSpot',
              companyUrl,
              category,
              languages,
              installCount: frameworkInstallCount || ecosystemInstallCount
            },
            support: {
              liveChatUrl,
              supportEmail,
              facebookUrl,
              builtByHubSpot,
              phone,
              supportLanguages,
              supportUrl
            },
            legal: {
              builtByHubSpot,
              privacyUrl1,
              privacyUrl2,
              privacyUrl3,
              termsUrl
            }
          }), productType === 'AGENT' && /*#__PURE__*/_jsx(ProductFeedbackCard, {
            partnerPortalId: partnerPortalId,
            listingId: listingId,
            logInUrl: logInUrl
          })]
        })
      }), compatibilityModalOpen && /*#__PURE__*/_jsx(FeatureCompatibilityModal, {
        features: featuresV2,
        installability: installability,
        onClose: () => setCompatibilityModalOpen(false)
      })]
    })
  });
}